import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Fish, TrendingUp, Droplets, Target } from "lucide-react";

export default function Fishing() {
  const t = useT();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/daily-logs", { params: { log_type: "fishing_log" } });
      setLogs(data); setLoading(false);
    })();
  }, []);

  const stats = {
    total: logs.length,
    excellent: logs.filter(l => l.fishing_outcome === "excellent").length,
    good: logs.filter(l => l.fishing_outcome === "good").length,
    caught: logs.reduce((s, l) => s + (l.kept_count || 0) + (l.released_count || 0), 0),
    avgRating: logs.length ? (logs.reduce((s, l) => s + (l.success_rating || 0), 0) / logs.length).toFixed(1) : "—",
  };

  // species aggregation
  const species = {};
  logs.forEach(l => {
    if (!l.caught_species) return;
    const key = l.caught_species.toLowerCase();
    species[key] = species[key] || { name: l.caught_species, count: 0, kept: 0, released: 0 };
    species[key].count += 1;
    species[key].kept += l.kept_count || 0;
    species[key].released += l.released_count || 0;
  });
  const speciesList = Object.values(species).sort((a, b) => b.count - a.count);

  // hot spots
  const spots = {};
  logs.forEach(l => {
    if (!l.place_name) return;
    spots[l.place_name] = spots[l.place_name] || { name: l.place_name, count: 0, totalRating: 0 };
    spots[l.place_name].count += 1;
    spots[l.place_name].totalRating += l.success_rating || 0;
  });
  const hotspots = Object.values(spots).map(s => ({ ...s, avgRating: (s.totalRating / s.count).toFixed(1) }))
    .sort((a, b) => b.avgRating - a.avgRating).slice(0, 6);

  return (
    <div className="space-y-6" data-testid="fishing-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#A78BFA]">{t("fishing.subtitle")}</div>
        <h2 className="font-display text-2xl font-bold tracking-tight">{t("fishing.title")}</h2>
        <p className="text-sm text-[#94A3B8] mt-1">{t("fishing.intro")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <FishStat icon={Fish} label={t("fishing.stat.trips")} value={stats.total} tone="purple" />
        <FishStat icon={Target} label={t("fishing.stat.landed")} value={stats.caught} tone="blue" />
        <FishStat icon={TrendingUp} label={t("fishing.stat.avgRating")} value={stats.avgRating} sub={t("fishing.avgRatingSub", { excellent: stats.excellent, good: stats.good })} tone="emerald" />
        <FishStat icon={Droplets} label={t("fishing.stat.species")} value={speciesList.length} tone="amber" />
      </div>

      {loading ? (
        <div className="h-64 ledger-card animate-pulse" />
      ) : logs.length === 0 ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]">
          <Fish className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">{t("fishing.empty")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="ledger-card p-5" data-testid="fishing-species">
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mb-3">{t("fishing.speciesTracker")}</div>
            <div className="space-y-2">
              {speciesList.map(s => (
                <div key={s.name} className="flex items-center justify-between p-3 bg-[#050A14] border border-[#1E293B] rounded-md">
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="flex gap-3 text-[11px] font-mono text-[#94A3B8]">
                    <span>{s.count} {t("fishing.trips")}</span>
                    <span className="text-[#34D399]">{s.kept} {t("fishing.kept")}</span>
                    <span className="text-[#4DA6FF]">{s.released} {t("fishing.released")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ledger-card p-5" data-testid="fishing-hotspots">
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mb-3">{t("fishing.hotSpots")}</div>
            <div className="space-y-2">
              {hotspots.map(s => (
                <div key={s.name} className="flex items-center justify-between p-3 bg-[#050A14] border border-[#1E293B] rounded-md">
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <span className="text-[#94A3B8]">{s.count} {t("fishing.visits")}</span>
                    <span className="text-[#FBBF24]">★ {s.avgRating}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="ledger-card p-5" data-testid="fishing-timeline">
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mb-3">{t("fishing.timeline")}</div>
        <div className="space-y-2">
          {logs.slice(0, 15).map(l => (
            <div key={l.id} className="flex items-center justify-between p-3 bg-[#050A14] border border-[#1E293B] rounded-md text-xs">
              <div>
                <div className="font-medium">{l.caught_species || "—"} · {l.place_name || "—"}</div>
                <div className="font-mono text-[10px] text-[#94A3B8]">{fmtDate(l.log_date)} · {l.bait_used || "—"} / {l.method_used || "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-[#FBBF24]">{"★".repeat(l.success_rating || 0)}</div>
                <div className="font-mono text-[10px] text-[#94A3B8]">{(l.kept_count || 0) + (l.released_count || 0)} {t("fishing.fish")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FishStat({ icon: Icon, label, value, sub, tone }) {
  const toneMap = {
    purple: "text-[#A78BFA] border-[#8B5CF6]/30 bg-[#8B5CF6]/10",
    blue: "text-[#4DA6FF] border-[#007AFF]/30 bg-[#007AFF]/10",
    emerald: "text-[#34D399] border-[#10B981]/30 bg-[#10B981]/10",
    amber: "text-[#FBBF24] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  };
  return (
    <div className="ledger-card p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${toneMap[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-mono text-[10px] tracking-widest text-[#94A3B8]">{label}</div>
      <div className="font-display text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-[#94A3B8] mt-1">{sub}</div>}
    </div>
  );
}
